// =============================================================================
// SANKEY NORMALIZER (DATA-FIRST PIPELINE FOR AI-GENERATED SANKEYS)
// =============================================================================

import type { GenerateSankeyToolOutput } from '@azymmetric/shared/tools/generate-sankey.tool-config';
import type { SankeyData } from '@components/shared/sankey.component';
import {
  type SankeyChartUtils,
  type SankeyColorScheme,
  SankeyDataBuilder,
  createSankeyChartUtils,
} from './sankey.util';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type NormalizerOptions = {
  colorScheme?: SankeyColorScheme; // Color scheme to use (default: 'default')
  minLinkValue?: number; // Minimum value for visibility (default: 3)
  scaleMode?: 'auto' | 'soft' | 'none'; // How to scale link values (default: 'auto')
  typeFormatters?: Record<string, (name: string) => string>; // Custom formatters for specific types
  typeOrder?: string[]; // Explicit left-to-right type order (default: inferred via topological sort)
};

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function normalizeType(type?: string): string {
  if (!type) return 'unknown';
  return type.trim().replace(/\s+/g, '_').toLowerCase();
}

function parseTypeAndName(idOrName: string, fallbackType: string): { type: string; name: string } {
  const idx = idOrName.indexOf(':');
  if (idx > 0) {
    const t = idOrName.slice(0, idx);
    const n = idOrName.slice(idx + 1);
    return { type: normalizeType(t), name: n };
  }
  return { type: normalizeType(fallbackType), name: idOrName };
}

function splitTypeAndName(idOrName: string): [string, string] {
  const idx = idOrName.indexOf(':');
  if (idx > 0) {
    return [idOrName.slice(0, idx), idOrName.slice(idx + 1)];
  }
  return ['unknown', idOrName];
}

function topologicalOrderByType(typeEdges: Array<{ from: string; to: string }>, allTypes: Set<string>): string[] {
  const inDegree = new Map<string, number>();
  const adj = new Map<string, Set<string>>();

  for (const t of allTypes) {
    inDegree.set(t, 0);
    adj.set(t, new Set());
  }

  for (const { from, to } of typeEdges) {
    if (from === to) continue;
    if (!adj.get(from)!.has(to)) {
      adj.get(from)!.add(to);
      inDegree.set(to, (inDegree.get(to) || 0) + 1);
    }
  }

  // Kahn's algorithm
  const queue: string[] = Array.from(allTypes).filter((t) => (inDegree.get(t) || 0) === 0);
  const order: string[] = [];

  while (queue.length) {
    const t = queue.shift()!;
    order.push(t);
    for (const nxt of adj.get(t) || []) {
      inDegree.set(nxt, (inDegree.get(nxt) || 0) - 1);
      if ((inDegree.get(nxt) || 0) === 0) queue.push(nxt);
    }
  }

  // If cycles or disconnected, append remaining types preserving insertion order
  if (order.length < allTypes.size) {
    for (const t of allTypes) if (!order.includes(t)) order.push(t);
  }
  return order;
}

function aggregateLinks(
  links: Array<{ source: string; target: string; value: number }>,
): Array<{ source: string; target: string; value: number }> {
  const acc = new Map<string, number>();
  for (const l of links) {
    const key = JSON.stringify([l.source, l.target]);
    acc.set(key, (acc.get(key) || 0) + (Number.isFinite(l.value) ? l.value : 0));
  }
  const out: Array<{ source: string; target: string; value: number }> = [];
  for (const [key, v] of acc.entries()) {
    const [src, tgt] = JSON.parse(key);
    out.push({ source: src, target: tgt, value: v });
  }
  return out;
}

function scaleLinkValues(
  links: Array<{ source: string; target: string; value: number }>,
  scaleMode: 'auto' | 'soft' | 'none',
  minLinkValue: number,
  nodeCount: number,
): Array<{ source: string; target: string; value: number }> {
  if (scaleMode === 'none' || links.length === 0) return links;

  const vals = links.map((l) => l.value);
  const minV = Math.min(...vals);
  const maxV = Math.max(...vals);
  const totalFlow = vals.reduce((sum, v) => sum + v, 0);

  if (scaleMode === 'soft') {
    // Light boost only when values are extremely small
    if (maxV <= minLinkValue) {
      return links.map((l) => ({ ...l, value: Math.max(l.value * 2, minLinkValue) }));
    }
    return links;
  }

  // auto mode - scale to match visual density of platform Sankeys
  // Platform Sankeys naturally have higher values through domain aggregation
  // We need to match that by scaling up small link values more aggressively

  // If all values are very small (typical for AI-generated data), scale them up significantly
  if (maxV <= 5) {
    // Aggressive scaling for uniformly small values
    const boostFactor = 20;
    return links.map((l) => ({ ...l, value: l.value * boostFactor }));
  }

  // If there's some variation but values are still small, moderate scaling
  if (maxV <= 20) {
    const boostFactor = 8;
    return links.map((l) => ({ ...l, value: l.value * boostFactor }));
  }

  // For already reasonable values, just ensure minimum
  return links.map((l) => ({ ...l, value: Math.max(l.value, minLinkValue) }));
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

export function normalizeToolOutput(
  input: GenerateSankeyToolOutput,
  options: NormalizerOptions = {},
): {
  chartUtils: SankeyChartUtils<string>;
  data: SankeyData<Record<string, unknown>, { type?: string }>;
} {
  // Use provided options or sensible defaults
  const minLinkValue = options.minLinkValue ?? 3;
  const scaleMode = options.scaleMode ?? 'auto';
  const colorScheme = options.colorScheme ?? 'default';

  // Step 1: canonicalize nodes
  const nodes = (input.nodes || []).map((n) => {
    const type = normalizeType((n as { type?: string }).type || n.category);
    const name = n.name;
    const id = `${type}:${name}`;
    return { id, name, type };
  });

  // Build helper maps
  const idToType = new Map(nodes.map((n) => [n.id, n.type] as const));
  const nameToType = new Map(nodes.map((n) => [n.name, n.type] as const));

  // Step 2: canonicalize + aggregate links
  const canonLinks = (input.links || []).map((l) => {
    const srcParsed = parseTypeAndName(
      String(l.source),
      idToType.get(String(l.source)) || nameToType.get(String(l.source)) || 'unknown',
    );
    const tgtParsed = parseTypeAndName(
      String(l.target),
      idToType.get(String(l.target)) || nameToType.get(String(l.target)) || 'unknown',
    );
    return {
      source: `${srcParsed.type}:${srcParsed.name}`,
      target: `${tgtParsed.type}:${tgtParsed.name}`,
      value: Number.isFinite(l.value) ? l.value : 1,
    };
  });

  const mergedLinks = aggregateLinks(canonLinks);

  // Step 3: determine typeOrder (explicit or inferred via topological sort)
  const allTypes = new Set(nodes.map((n) => n.type));
  let typeOrder: string[];

  if (options.typeOrder) {
    // Use explicit ordering if provided
    typeOrder = options.typeOrder;
  } else {
    // Infer ordering from link structure using topological sort
    const typeEdges = mergedLinks.map((l) => {
      const [srcType] = splitTypeAndName(l.source);
      const [tgtType] = splitTypeAndName(l.target);
      return { from: srcType, to: tgtType };
    });
    typeOrder = topologicalOrderByType(typeEdges, allTypes);
  }

  // Step 4: scaling
  const scaledLinks = scaleLinkValues(mergedLinks, scaleMode, minLinkValue, nodes.length);

  // Step 5: build with SankeyDataBuilder
  const builder = new SankeyDataBuilder<{ type?: string }>();

  // Sort nodes by typeOrder (column position), then alphabetically by name
  const typeToIndex = new Map<string, number>();
  typeOrder.forEach((t, i) => typeToIndex.set(t, i));

  const nodesSorted = nodes.slice().sort((a, b) => {
    const ai = typeToIndex.has(a.type) ? typeToIndex.get(a.type)! : Number.MAX_SAFE_INTEGER;
    const bi = typeToIndex.has(b.type) ? typeToIndex.get(b.type)! : Number.MAX_SAFE_INTEGER;
    if (ai !== bi) return ai - bi;
    return a.name.localeCompare(b.name);
  });

  for (const n of nodesSorted) {
    builder.addNode({ name: n.name, type: n.type, extraData: { type: n.type } });
  }

  for (const l of scaledLinks) {
    const [srcType, srcName] = splitTypeAndName(l.source);
    const [tgtType, tgtName] = splitTypeAndName(l.target);
    builder.addLink({
      sourceName: srcName,
      sourceType: srcType,
      targetName: tgtName,
      targetType: tgtType,
      value: l.value,
    });
  }

  const sankeyData = builder.getData();

  // Extract all unique types from the data for color mapping
  const uniqueTypes = Array.from(new Set(sankeyData.nodes.map((n) => n.category || 'default')));

  // Create chart utilities for formatting and coloring
  const chartUtils = createSankeyChartUtils(colorScheme, {
    typeFormatters: options.typeFormatters,
    allTypes: uniqueTypes, // Pre-populate the color cache with all types
  });

  return {
    data: sankeyData,
    chartUtils,
  };
}
